import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AsyncSelect, components } from "../../components/select";
import SortablePeopleList from "../../components/sortablePeople";
import { searchSeriesOptions } from "../../helper";
import { foldForSearch, rankMatches } from "../../helper/searchFold";

const OPTION_STYLES = {
  container: { display: "flex", alignItems: "center", gap: "10px" },
  image: { width: "30px", height: "30px", objectFit: "cover", borderRadius: "50%" },
  name: { margin: 0 },
};

// Module scope so it isn't a new component type each render (which would
// remount the menu mid-interaction).
const Option = React.memo((props) => {
  const { image, label } = props.data || {};
  return (
    <components.Option {...props}>
      <div style={OPTION_STYLES.container}>
        {image && <img src={image} alt={label} style={OPTION_STYLES.image} />}
        <p style={OPTION_STYLES.name}>{label}</p>
      </div>
    </components.Option>
  );
});

const SELECT_COMPONENTS = { Option };

// A recommended entry is a book OR a series, identified by whichever id it carries.
const idOf = (item) => item.bookId || item.seriesId;

const bookLabel = (book) => (book.authorName ? `${book.label} — ${book.authorName}` : book.label);
const seriesLabel = (name, count) => (count ? `${name} — ${count} books` : name);


/**
 * The Recommended Books field of the recommender form, laid out like the Picks
 * editor's shelf: a search that finds books AND series, and the chosen ones as
 * a grid of covers whose order is the order the app shows them in.
 *
 * `items`: the translation's `recommendedBooks` — `{ bookId, bookName }` or
 *   `{ seriesId, seriesName }`.
 * `bookOptions`: the form's preloaded book list (label, value, authorName,
 *   image) — books are searched in it and their covers come from it.
 * `onChange(nextItems)`: the full list, in order.
 */
const RecommendedBooksField = ({
  items = [],
  bookOptions = [],
  token,
  onChange,
  error,
  // Books Written uses the same shelf; only the words differ.
  label,
  hint = "This is the order they appear in the app. Drag to reorder, or type a position to move a book straight there.",
}) => {
  const { t } = useTranslation();
  // Series picked or loaded this session, so a new pick shows its cover and
  // book count before the form has been saved and reloaded.
  const [seriesCache, setSeriesCache] = useState({});

  const loadOptions = async (input) => {
    const term = foldForSearch((input || "").trim());
    if (term.length < 2) return [];

    // Best match first: exact title, starts with, word starts with, the rest.
    const books = rankMatches(bookOptions, term)
      .slice(0, 100)
      .map((book) => ({ ...book, label: bookLabel(book) }));

    // Searched in English whatever language the form is on: the edit form
    // saves blank titles for languages a series wasn't given, so another
    // language can match nothing or show a nameless row. The server names the
    // entry per language on save.
    const series = await searchSeriesOptions(input, token);
    setSeriesCache((prev) => ({ ...prev, ...Object.fromEntries(series.map((s) => [s.value, s])) }));

    // Grouped rather than mixed in: a card that says "9 books" beside one that
    // is a book needs the heading to explain itself.
    return series.length
      ? [{ label: "Books", options: books }, { label: "Series", options: series }]
      : books;
  };

  // ~11,000 books; built once per list, not on every keystroke elsewhere in the form.
  const bookById = useMemo(() => new Map(bookOptions.map((book) => [String(book.value), book])), [bookOptions]);

  const toOption = (item) => {
    if (item.seriesId) {
      const cached = seriesCache[item.seriesId];
      const name = item.seriesName || cached?.seriesName || "(unknown series)";
      return {
        value: item.seriesId,
        label: seriesLabel(name, item.bookCount ?? cached?.bookCount),
        image: item.image || cached?.image,
        isSeries: true,
      };
    }
    const book = bookById.get(String(item.bookId));
    return {
      value: item.bookId,
      label: book ? bookLabel({ ...book, label: item.bookName || book.label }) : item.bookName || "(unknown)",
      image: book?.image,
    };
  };

  const selectedOptions = items.map(toOption);

  const toItem = (option) =>
    option.isSeries
      ? {
        seriesId: option.value,
        seriesName: option.seriesName,
        // Display-only: not in the schema, so the server drops them on save.
        bookCount: option.bookCount,
        image: option.image,
      }
      : { bookId: option.value, bookName: bookOptions.find((b) => b.value === option.value)?.label || option.label };

  // react-select appends a pick to the end of its value array, which on a long
  // shelf drops it out of view. New picks go to the top; the rest keep the
  // order they were dragged into.
  const handlePick = (options) => {
    const picked = options || [];
    const existingIds = new Set(items.map(idOf));
    const pickedIds = new Set(picked.map((o) => o.value));
    const added = picked.filter((o) => !existingIds.has(o.value)).map(toItem);
    const kept = items.filter((item) => pickedIds.has(idOf(item)));
    onChange([...added, ...kept]);
  };

  // The grid hands back the exact order it wants (drag, position box,
  // top/bottom, remove), so it is taken literally — reordering the stored
  // entries by id keeps fields the options don't carry.
  const handleReorder = (options) => {
    const byId = new Map(items.map((item) => [idOf(item), item]));
    onChange((options || []).map((o) => byId.get(o.value)).filter(Boolean));
  };

  return (
    <div className="mb-3">
      <label className="label">{label ?? t("recommender.recommendedBooks")}:</label>
      {/* Add-only: the chips are hidden, so the grid below is the one place
          that shows order and removes. Clearing and backspace are disarmed for
          the same reason — with no chips they would delete rows unseen. */}
      <AsyncSelect
        className="form-control-md fw-medium p-0"
        isMulti
        markAdded
        isClearable={false}
        backspaceRemovesValue={false}
        cacheOptions
        defaultOptions={false}
        controlShouldRenderValue={false}
        loadOptions={loadOptions}
        placeholder="Search books or series by title..."
        noOptionsMessage={({ inputValue }) =>
          inputValue.trim().length < 2 ? "Type at least two letters" : `No books or series found for "${inputValue}"`
        }
        value={selectedOptions}
        onChange={handlePick}
        components={SELECT_COMPONENTS}
      />
      <div className="mt-2">
        <SortablePeopleList
          items={selectedOptions}
          onChange={handleReorder}
          thumbShape="cover"
          variant="grid"
          emptyMessage="No books selected yet — search above."
        />
      </div>
      <small className="text-muted">{hint}</small>
      {error && <p className="error-message d-block">{error}</p>}
    </div>
  );
};

export default RecommendedBooksField;
